import { useState } from "react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import {
  ARTIST_NAME,
  SITE_LOCATION,
  SITE_GENRE_TAGS,
  MANAGEMENT_NAME,
  MUSIC_VIDEO_YOUTUBE_ID,
  MUSIC_VIDEO_TITLE,
  EPK_STATS,
  EPK_COLLABORATIONS,
  EPK_BANNER_TEXT,
  FESTIVALS,
  PERFORMANCES,
  LIVE_VIDEO_YOUTUBE_IDS,
  BOOKING_STATUS,
  BOOKING_COPY,
  BOOKING_EMAIL,
  BOOKING_FORM_ENDPOINT,
  SOCIALS,
  BIO,
  EPK_GALLERY,
  LOGO,
} from "@/data/site";

// EPK gallery — press photos in the order they're shown, edited in the
// site editor (src/content/epk.json).

// Booking form — the template's standard field set. Sends to Formspree
// (BOOKING_FORM_ENDPOINT), which emails each inquiry to her management inbox
// and keeps a copy in its dashboard. The visitor sees a real confirmation or
// a real error (with the email address as a fallback), never a fake success.
const EMPTY_BOOKING = { name: "", email: "", event: "", dateLocation: "", budget: "", details: "" };

const BookingForm = () => {
  const [fields, setFields] = useState(EMPTY_BOOKING);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const r = await fetch(BOOKING_FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          "First & Last Name": fields.name,
          email: fields.email,
          "Event/Festival": fields.event,
          "Event Date(s) & Location": fields.dateLocation,
          "Estimated Budget Range": fields.budget,
          "Additional Details": fields.details,
          _subject: `Booking Inquiry: ${fields.event || ARTIST_NAME}`,
        }),
      });
      if (!r.ok) throw new Error(String(r.status));
      setStatus("sent");
      setFields(EMPTY_BOOKING);
    } catch {
      setStatus("error");
    }
  };

  if (status === "sent") {
    return (
      <div role="status" className="mt-8 rounded-lg border border-[var(--sm-gold)]/30 bg-black/40 px-6 py-8 text-center">
        <p className="font-display text-xl text-white">Thank you, your inquiry was sent.</p>
        <p className="font-body mt-2 text-sm text-white/60">We review all inquiries personally and respond with next steps.</p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="font-body mt-4 text-xs text-[var(--sm-gold-light)] underline decoration-[var(--sm-gold)]/40 underline-offset-2 hover:text-white"
        >
          Send another inquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 grid grid-cols-1 gap-4 text-left sm:grid-cols-2">
      <input
        required
        placeholder="First & Last Name*"
        value={fields.name}
        onChange={set("name")}
        className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/40 focus:border-[var(--sm-gold)]/60 focus:outline-none"
      />
      <input
        required
        type="email"
        placeholder="Email*"
        value={fields.email}
        onChange={set("email")}
        className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/40 focus:border-[var(--sm-gold)]/60 focus:outline-none"
      />
      <input
        required
        placeholder="Event/Festival*"
        value={fields.event}
        onChange={set("event")}
        className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/40 focus:border-[var(--sm-gold)]/60 focus:outline-none sm:col-span-2"
      />
      <input
        required
        placeholder="Event Date(s) & Location*"
        value={fields.dateLocation}
        onChange={set("dateLocation")}
        className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/40 focus:border-[var(--sm-gold)]/60 focus:outline-none sm:col-span-2"
      />
      <input
        placeholder="Estimated Budget Range"
        value={fields.budget}
        onChange={set("budget")}
        className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/40 focus:border-[var(--sm-gold)]/60 focus:outline-none sm:col-span-2"
      />
      <textarea
        placeholder="Additional Details"
        value={fields.details}
        onChange={set("details")}
        rows={3}
        className="rounded-lg border border-white/15 bg-black/40 px-4 py-2.5 font-body text-sm text-white placeholder:text-white/40 focus:border-[var(--sm-gold)]/60 focus:outline-none sm:col-span-2"
      />
      <button
        type="submit"
        disabled={status === "sending"}
        className="mt-1 rounded-full bg-[var(--sm-gold)] px-8 py-3 font-body text-sm font-medium text-black transition-colors hover:bg-[var(--sm-gold-light)] disabled:opacity-60 sm:col-span-2"
      >
        {status === "sending" ? "Sending…" : "Submit Booking Inquiry"}
      </button>
      {status === "error" && (
        <p role="alert" className="font-body text-sm text-red-300 sm:col-span-2">
          Sorry, that didn't go through. Please try again, or email{" "}
          <a href={`mailto:${BOOKING_EMAIL}`} className="underline underline-offset-2">
            {BOOKING_EMAIL}
          </a>
          .
        </p>
      )}
      <p className="font-body text-xs text-white/40 sm:col-span-2">
        We review all inquiries personally and respond with next steps.
      </p>
    </form>
  );
};

const PERFORMANCES_PREVIEW = 8;

const PerformanceList = () => {
  const [all, setAll] = useState(false);
  const shown = all ? PERFORMANCES : PERFORMANCES.slice(0, PERFORMANCES_PREVIEW);
  return (
    <div className="mt-4">
      <ul className="divide-y divide-white/10 border-y border-white/10 text-left">
        {shown.map((p) => (
          <li key={`${p.date}-${p.event}`} className="flex flex-col gap-0.5 py-2.5 sm:flex-row sm:items-baseline sm:gap-4">
            <span className="font-accent w-28 shrink-0 text-xs uppercase tracking-[0.12em] text-[var(--sm-gold)]">{p.date}</span>
            <span className="font-body text-sm text-white/80">
              {p.event} <span className="text-white/45">· {p.place}</span>
            </span>
          </li>
        ))}
      </ul>
      {PERFORMANCES.length > PERFORMANCES_PREVIEW && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="font-accent mt-3 text-xs uppercase tracking-[0.18em] text-white/55 underline decoration-white/20 underline-offset-4 transition-colors hover:text-[var(--sm-gold-light)]"
        >
          {all ? "Show fewer" : `Show all ${PERFORMANCES.length}`}
        </button>
      )}
    </div>
  );
};

export default function EPK() {
  return (
    <div className="starfield relative min-h-screen">
      <Navbar />
      <main className="relative mx-auto max-w-4xl px-6 pb-10 pt-40 text-center">
        {/* Header / info */}
        <Reveal>
          <span className="overline">Electronic Press Kit</span>
          <h1 className="mt-6 flex justify-center">
            <img src={LOGO.wordmark} alt={ARTIST_NAME} className="w-[20rem] max-w-full sm:w-[26rem]" />
          </h1>
          <p className="font-accent mt-4 text-sm uppercase tracking-[0.25em] text-white/50">
            {SITE_GENRE_TAGS} · {SITE_LOCATION}
          </p>
          <div className="font-body mx-auto mt-6 max-w-2xl space-y-3 text-sm font-light leading-relaxed text-white/65">
            {BIO.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <p className="font-accent mt-10 text-sm uppercase tracking-[0.2em] text-[var(--sm-gold)]">{EPK_BANNER_TEXT}</p>
        </Reveal>

        {/* Slow-scrolling festival-name ticker — genuinely slow, ambient,
            readable at a glance without needing to track it. */}
        <Reveal delay={0.12}>
          <div
            className="mt-8 overflow-hidden"
            style={{
              WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
              maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
            }}
          >
            <div className="marquee-track">
              {[...FESTIVALS, ...FESTIVALS].map((f, i) => (
                <span
                  key={i}
                  className="font-accent shrink-0 px-6 text-xs uppercase tracking-[0.25em] text-white/35"
                >
                  {f}
                </span>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-accent text-xs uppercase tracking-[0.2em] text-white/55 transition-colors hover:text-[var(--sm-gold-light)]"
              >
                {s.label}
              </a>
            ))}
          </div>
        </Reveal>

      </main>

      {/* Two-column layout: all EPK info — video, stats, performances,
          booking form (left) / gallery (right, smaller thumbnails) — mirrored
          from the template so it doesn't read as a Sierra clone — so the booking CTA is reachable without
          scrolling past a large gallery block first. On mobile, the info
          column renders first (order-1) for the same reason; the gallery
          sits beside the info only at the lg breakpoint. */}
      <div className="relative mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 pb-32 lg:grid-cols-[1.15fr_1fr] lg:items-start lg:gap-14">
        {/* Thin gold divider between the gallery and info column, styled
            like a subtle spotlight beam (angled, tapering at both ends) —
            desktop only, where the two-column layout actually applies. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 z-10 hidden w-px lg:block"
          style={{
            left: "55.5%",
            transform: "rotate(-4deg)",
            background: "linear-gradient(to bottom, transparent 0%, rgba(255, 46, 151,0.32) 20%, rgba(255, 46, 151,0.32) 80%, transparent 100%)",
          }}
        />
        <Reveal className="order-2" delay={0.2}>
          <div className="columns-3 gap-2 sm:columns-4 lg:columns-3">
            {EPK_GALLERY.map((src) => (
              <div key={src} className="mb-2 break-inside-avoid overflow-hidden rounded-lg border border-white/10 bg-black/40">
                <img src={src} alt={ARTIST_NAME} className="w-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </Reveal>

        <div className="order-1 text-center lg:text-left">
          {/* Released music video leads the info column (no dedicated
              live-performance reel was supplied — swap for one when it is). */}
          <Reveal delay={0.1}>
            <div className="overflow-hidden rounded-xl border border-white/10 shadow-[0_40px_140px_-30px_rgba(255,46,151,0.3)]">
              <iframe
                data-testid="epk-video-embed"
                className="aspect-video w-full"
                src={`https://www.youtube.com/embed/${MUSIC_VIDEO_YOUTUBE_ID}`}
                title={MUSIC_VIDEO_TITLE ? `Jenn Rock — ${MUSIC_VIDEO_TITLE}` : "Jenn Rock — music video"}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {EPK_STATS.map((s) => (
                <div key={s.label}>
                  <p className="font-display text-4xl text-[var(--sm-gold-light)]">{s.value}</p>
                  <p className="font-body mt-1 text-xs uppercase tracking-[0.15em] text-white/50">{s.label}</p>
                </div>
              ))}
            </div>
            {/* Jumps to the booking form below. */}
            <a
              href="#booking"
              data-testid="epk-book-cta"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="mt-8 inline-flex items-center rounded-full bg-[var(--sm-gold)] px-8 py-3 font-body text-sm font-medium text-black transition-colors hover:bg-[var(--sm-gold-light)]"
            >
              Book {ARTIST_NAME}
            </a>
          </Reveal>

          {/* Performance history (her full list, newest first) + Collaborations.
              Shows the latest few with a toggle, so the booking form stays
              close to the top of the column. */}
          <Reveal delay={0.2}>
            <div className="mt-10">
              <span className="overline">Performances</span>
              <PerformanceList />
              <p className="font-accent mt-8 text-xs uppercase tracking-[0.15em] text-[var(--sm-gold)]">Collaborations</p>
              <p className="font-body mt-2 text-sm leading-relaxed text-white/70">{EPK_COLLABORATIONS.join(" · ")}</p>
            </div>
          </Reveal>

          {/* Live — her performance videos (YouTube shows each title). */}
          {LIVE_VIDEO_YOUTUBE_IDS.length > 0 && (
            <Reveal delay={0.2}>
              <div className="mt-10">
                <span className="overline">Live</span>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {LIVE_VIDEO_YOUTUBE_IDS.map((id) => (
                    <div key={id} className="overflow-hidden rounded-xl border border-white/10">
                      <iframe
                        className="aspect-video w-full"
                        src={`https://www.youtube.com/embed/${id}`}
                        title="Jenn Rock — live"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          )}

          {/* Booking — directly beneath the info column, no gallery block
              between it and the rest of the page content. */}
          <Reveal delay={0.25}>
            <div id="booking" className="mt-10 scroll-mt-28 rounded-xl border border-[var(--sm-gold)]/30 px-6 py-8 sm:px-8">
              <h2 className="font-display text-2xl text-white sm:text-3xl">Submit a Booking Request</h2>
              <p className="font-accent mt-2 text-sm italic text-white/60">{BOOKING_STATUS}</p>
              <p className="font-body mt-1 text-xs text-white/45">{BOOKING_COPY}</p>
              <p className="font-body mt-1 text-xs text-white/45">Management: {MANAGEMENT_NAME}</p>
              <BookingForm />
              <p className="font-body mt-6 text-xs text-white/55">
                Prefer to reach out directly? Email:{" "}
                <a
                  href={`mailto:${BOOKING_EMAIL}`}
                  className="text-[var(--sm-gold-light)] underline decoration-[var(--sm-gold)]/40 underline-offset-2 transition-colors hover:text-white"
                >
                  {BOOKING_EMAIL}
                </a>
              </p>
            </div>
          </Reveal>
        </div>
      </div>
      <Footer />
    </div>
  );
}
